import _ from "lodash";

export interface Outcome {
  // API may return arbitrary strings (e.g., hex colors); we normalize below.
  scoreLabelColor?: string;
  result?: boolean;
  name?: string;
  formula?: string;
  recommendations?: string[];
  scoreLabel?: string;
}

export interface HistoryEntry {
  formula?: string;
  result?: boolean;
  explanation?: any;
}

export function deriveOutcomeColor(
  outcome: Outcome | null | undefined
): "pass" | "fail" | "warn" {
  const color = outcome?.scoreLabelColor;
  if (color === "pass" || color === "fail" || color === "warn") {
    return color;
  }
  if (outcome?.result === false) return "fail";
  if (outcome?.result === true) return "pass";
  return "warn";
}

function findRangeIndex(ranges, score) {
  return (
    _.findIndex(ranges, (range) => _.inRange(score, range[0], range[1] + 1)) ||
    0
  );
}

function calculateRangeWeight(ranges, score) {
  const rangeIndex = findRangeIndex(ranges, score);
  return (score / 100) * ((rangeIndex + 1) / _.size(ranges));
}

function calculateAngle(ranges, score) {
  return _.round(120 * calculateRangeWeight(ranges, score), 2);
}

export function getGradientColorForLumenRun(lumenRun) {
  const ranges = lumenRun?.settings?.scoreLabels
    ? _.orderBy(_.values(lumenRun.settings.scoreLabels), (range) =>
        _.max(_.take(range, 2))
      )
    : [
        [0, 99],
        [100, 100],
      ];
  const deg = calculateAngle(ranges, lumenRun?.score);

  return deg ? `hsl(${deg} 50% 50%)` : "white";
}

export function getScoreLabelStyle(scoreLabelColor, defaultColor = "black") {
  if (scoreLabelColor && !_.startsWith(scoreLabelColor, "#")) {
    return {
      className: scoreLabelColor,
    };
  }

  return {
    style: {
      color: scoreLabelColor || defaultColor,
    },
  };
}

export function getColorForLumenRun(lumenRun) {
  return getScoreLabelStyle(
    lumenRun?.scoreLabelColor,
    getGradientColorForLumenRun(lumenRun)
  );
}

export function findLumenResult(lumenRun, applicant = undefined) {
  return (
    _.find(
      lumenRun?.results,
      applicant
        ? (result) => _.includes(result?.applicant, applicant)
        : "application"
    ) || {}
  );
}

export function queryEvaluationHistory(history, query, key = undefined) {
  const entry = _.find(history, (entry) => _.startsWith(entry?.formula, query));
  return key ? _.get(entry, key) : entry;
}

export function queryByAnnotation(result, annotations, key: any = "result") {
  return _.chain(annotations)
    .castArray()
    .map((annotation) =>
      queryEvaluationHistory(
        result?.evaluation?.history,
        `#${annotation}:`,
        key
      )
    )
    .reject(_.isNil)
    .first()
    .value();
}

export function normalizeOutcomesToArray(
  outcomes: any[] | Record<string, any> | undefined | null
): Outcome[] {
  if (!outcomes) return [];

  if (Array.isArray(outcomes)) {
    // TODO(stefan): Rename name to formula and remove this logic.
    return _.has(outcomes[0], "name")
      ? _.map(outcomes, (o) => ({ ...o, formula: o.name || o.formula }))
      : outcomes;
  }

  return _.map(outcomes, (outcome, formula) => ({
    ...outcome,
    formula,
    name: outcome.name || formula,
  }));
}

export function getCheckStatusFromOutcomes(
  entry: HistoryEntry,
  triggeredOutcomes: string[] = [],
  outcomes: Outcome[] | Record<string, Outcome> = []
): "pass" | "fail" | "warn" {
  const formulaKey = entry?.formula?.split(":")[0];

  if (!formulaKey || !formulaKey.startsWith("#")) {
    return entry?.result ? "pass" : "fail";
  }

  if (!triggeredOutcomes.includes(formulaKey)) {
    return entry?.result ? "pass" : "fail";
  }

  const matchingOutcome = Array.isArray(outcomes)
    ? outcomes.find(
        (outcome) =>
          outcome?.name === formulaKey || outcome?.formula === formulaKey
      )
    : outcomes?.[formulaKey];

  if (matchingOutcome) {
    return deriveOutcomeColor(matchingOutcome);
  }

  return entry?.result ? "pass" : "fail";
}

export function stripPriorityPrefix(scoreLabel?: string) {
  if (!scoreLabel) return scoreLabel;
  return scoreLabel.replace(/^p\d+-/, "");
}

/** Acronyms the client writes in caps and leasing reads in caps. */
const LABEL_ACRONYMS = new Set([
  "SSN",
  "ID",
  "DOB",
  "OFAC",
  "FCRA",
  "BGCC",
  "PDF",
  "NSF",
  "LLC",
  "US",
  "USA",
]);

const SMALL_LABEL_WORDS = new Set([
  "a",
  "an",
  "and",
  "at",
  "by",
  "for",
  "in",
  "of",
  "on",
  "or",
  "the",
  "to",
  "with",
]);

/**
 * The display form of a client-configured outcome name. Lumen stores these as
 * the client authored them — often shouted and carrying a `p0-`/`p1-` priority
 * prefix used only for ordering — so every surface that shows one to a person
 * reads it through here. Only an all-caps word is recased, so a name the
 * client deliberately mixed-cased survives untouched; acronyms stay whole.
 *
 * Display only. The raw value is what the wire, the stored pick and every
 * name-matching comparison still use.
 */
export const displayName = <T extends string | null | undefined>(
  label: T
): T => {
  if (!label) return label;
  const stripped = (stripPriorityPrefix(label) || label).trim();
  if (!stripped) return label;
  if (/[a-z]/.test(stripped)) return stripped as T;
  return stripped
    .split(/(\s+)/)
    .map((token, index) => {
      if (!token.trim()) return token;
      const [, lead, core, tail] = /^([^A-Za-z0-9]*)(.*?)([^A-Za-z0-9]*)$/.exec(
        token
      ) as RegExpExecArray;
      if (!core) return token;
      if (LABEL_ACRONYMS.has(core)) return token;
      const lower = core.toLowerCase();
      const cased =
        index > 0 && SMALL_LABEL_WORDS.has(lower)
          ? lower
          : lower.charAt(0).toUpperCase() + lower.slice(1);
      return `${lead}${cased}${tail}`;
    })
    .join("") as T;
};

// warn/fail bands, deduped + severity-sorted; shared by OutcomesV2 and the banner strip.
// VERO1-derived runs return checklist-reconciled outcomes post lumen#272 (empty before).
export function selectOutcomeBands(lumenRun): Outcome[] {
  if (
    !lumenRun?.outcomes ||
    !lumenRun?.settings?.outcomes ||
    !lumenRun?.results
  ) {
    return [];
  }
  const flagged = normalizeOutcomesToArray(lumenRun.outcomes).filter(
    (o) => o.scoreLabelColor === "warn" || o.scoreLabelColor === "fail"
  );
  const bands = _.map(
    _.groupBy(flagged, (o) => o.scoreLabel),
    (items) => ({
      ...items[0],
      recommendations: _.uniq(
        _.flatMap(items, (i) => i.recommendations || []).filter(
          (r) => _.isString(r) && r.trim() !== ""
        )
      ),
    })
  );
  const severityOrder = { fail: 1, warn: 2 };
  return _.sortBy(bands, (b) => severityOrder[b.scoreLabelColor] || 99);
}

export function partitionOutcomeBands(lumenRun): {
  verdict?: Outcome;
  caveats: Outcome[];
} {
  const verdictLabel = stripPriorityPrefix(lumenRun?.scoreLabel);
  const [verdict, caveats] = _.partition(
    selectOutcomeBands(lumenRun),
    (b) =>
      Boolean(verdictLabel) &&
      stripPriorityPrefix(b.scoreLabel) === verdictLabel
  );
  return { verdict: verdict[0], caveats };
}

// "p0-INCOME REVIEW: NEEDS REVIEW" → "Income Review: Needs Review"
export function titleCaseLabel(label?: string) {
  const stripped = stripPriorityPrefix(label);
  if (!stripped) return stripped;
  return stripped
    .toLowerCase()
    .replace(/(^|[\s(\-/])([a-z])/g, (m, lead, ch) => lead + ch.toUpperCase());
}
